/**
 * DiscountCodesPanel — MADE-252
 *
 * Staff interface for creating, editing, and deactivating discount codes.
 * Rendered inside ShopAdminPage under the "Discount Codes" tab.
 *
 * API contracts (Bridge / MADE-250):
 *   GET    /api/madebb/shop-admin/discount-codes
 *   POST   /api/madebb/shop-admin/discount-codes
 *   PATCH  /api/madebb/shop-admin/discount-codes/:id
 *   PATCH  /api/madebb/shop-admin/discount-codes/:id/deactivate
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import RefreshIcon from '@mui/icons-material/Refresh';
import { authFetch } from '../utils/authFetch';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DiscountType =
  | 'order_pct'
  | 'order_flat'
  | 'shipping_pct'
  | 'item_pct'
  | 'item_flat';

export interface DiscountCode {
  id: string | number;
  code: string;
  discountType: DiscountType;
  discountValue: number;
  expiresAt: string | null;
  maxUses: number | null;
  currentUses: number;
  isActive: boolean;
  description: string | null;
}

interface DiscountCodeForm {
  code: string;
  discountType: DiscountType;
  discountValue: string;  // string for controlled input
  expiresAt: string;      // ISO date string or ''
  maxUses: string;        // string for controlled input
  description: string;
}

interface FormErrors {
  code?: string;
  discountValue?: string;
  discountType?: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DISCOUNT_TYPES: { value: DiscountType; label: string }[] = [
  { value: 'order_pct',    label: '% off order'    },
  { value: 'order_flat',   label: 'Flat off order'  },
  { value: 'shipping_pct', label: '% off shipping'  },
  { value: 'item_pct',     label: '% off items'     },
  { value: 'item_flat',    label: 'Flat off items'  },
];

const DISCOUNT_CODES_API = '/api/madebb/admin/discount-codes';

const EMPTY_FORM: DiscountCodeForm = {
  code: '',
  discountType: 'order_pct',
  discountValue: '',
  expiresAt: '',
  maxUses: '',
  description: '',
};

const PCT_TYPES: DiscountType[] = ['order_pct', 'shipping_pct', 'item_pct'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getTypeLabel(type: DiscountType): string {
  return DISCOUNT_TYPES.find(d => d.value === type)?.label ?? type;
}

function formatValue(type: DiscountType, value: number): string {
  if (PCT_TYPES.includes(type)) return `${value}%`;
  return `$${value.toFixed(2)} BBD`;
}

function formatExpiry(expiresAt: string | null): string {
  if (!expiresAt) return 'No expiry';
  return new Date(expiresAt).toLocaleDateString('en-BB', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

function validateForm(form: DiscountCodeForm): FormErrors {
  const errs: FormErrors = {};
  if (!form.code.trim()) {
    errs.code = 'Code is required';
  }
  const val = parseFloat(form.discountValue);
  if (!form.discountValue || isNaN(val) || val <= 0) {
    errs.discountValue = 'Discount value must be greater than 0';
  } else if (PCT_TYPES.includes(form.discountType) && val > 100) {
    errs.discountValue = 'Percentage discounts cannot exceed 100%';
  }
  return errs;
}

// ---------------------------------------------------------------------------
// DiscountCodeFormDialog
// ---------------------------------------------------------------------------

interface FormDialogProps {
  open: boolean;
  editTarget: DiscountCode | null;
  onClose: () => void;
  onSaved: (code: DiscountCode) => void;
}

const DiscountCodeFormDialog: React.FC<FormDialogProps> = ({ open, editTarget, onClose, onSaved }) => {
  const isEdit = editTarget !== null;
  const [form, setForm] = useState<DiscountCodeForm>(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);

  // Populate form when editing an existing code
  useEffect(() => {
    if (open) {
      setServerError(null);
      setFormErrors({});
      if (editTarget) {
        setForm({
          code: editTarget.code,
          discountType: editTarget.discountType,
          discountValue: String(editTarget.discountValue),
          expiresAt: editTarget.expiresAt
            ? new Date(editTarget.expiresAt).toISOString().split('T')[0]
            : '',
          maxUses: editTarget.maxUses != null ? String(editTarget.maxUses) : '',
          description: editTarget.description ?? '',
        });
      } else {
        setForm(EMPTY_FORM);
      }
    }
  }, [open, editTarget]);

  const setField = (field: keyof DiscountCodeForm) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    if (formErrors[field as keyof FormErrors]) {
      setFormErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleTypeChange = (e: SelectChangeEvent<DiscountType>) => {
    setForm(prev => ({ ...prev, discountType: e.target.value as DiscountType }));
  };

  const handleSave = async () => {
    const errs = validateForm(form);
    if (Object.keys(errs).length > 0) {
      setFormErrors(errs);
      return;
    }

    setSaving(true);
    setServerError(null);

    const payload: Record<string, unknown> = {
      code: form.code.trim().toUpperCase(),
      discountType: form.discountType,
      discountValue: parseFloat(form.discountValue),
      ...(form.expiresAt ? { expiresAt: new Date(form.expiresAt).toISOString() } : {}),
      ...(form.maxUses ? { maxUses: parseInt(form.maxUses, 10) } : {}),
      ...(form.description.trim() ? { description: form.description.trim() } : {}),
    };

    try {
      const url = isEdit
        ? `${DISCOUNT_CODES_API}/${editTarget!.id}`
        : DISCOUNT_CODES_API;
      const method = isEdit ? 'PATCH' : 'POST';

      const res = await authFetch(url, {
        method,
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.error === 'CODE_EXISTS') {
          setFormErrors(prev => ({ ...prev, code: 'A code with this name already exists' }));
        } else {
          setServerError(data.message ?? `Server error (${res.status})`);
        }
        return;
      }

      const data = await res.json();
      onSaved(data.code);
    } catch (err: unknown) {
      setServerError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      data-testid="discount-code-dialog"
    >
      <DialogTitle>{isEdit ? `Edit — ${editTarget?.code}` : 'Add Discount Code'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {serverError && (
            <Alert severity="error" data-testid="dialog-server-error">{serverError}</Alert>
          )}

          {/* Code string */}
          <TextField
            label="Code"
            value={form.code}
            onChange={setField('code')}
            required
            fullWidth
            error={!!formErrors.code}
            helperText={formErrors.code}
            inputProps={{
              'data-testid': 'discount-code-input',
              style: { textTransform: 'uppercase' },
            }}
            disabled={saving || isEdit} // code string is immutable on edit
          />

          {/* Discount type */}
          <FormControl fullWidth required>
            <InputLabel id="discount-type-label">Discount Type</InputLabel>
            <Select<DiscountType>
              labelId="discount-type-label"
              label="Discount Type"
              value={form.discountType}
              onChange={handleTypeChange}
              inputProps={{ 'data-testid': 'discount-type-select' }}
              disabled={saving}
            >
              {DISCOUNT_TYPES.map(d => (
                <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Discount value */}
          <TextField
            label={PCT_TYPES.includes(form.discountType) ? 'Value (%)' : 'Value (BBD)'}
            value={form.discountValue}
            onChange={setField('discountValue')}
            required
            fullWidth
            type="number"
            inputProps={{
              min: 0,
              step: PCT_TYPES.includes(form.discountType) ? 1 : 0.01,
              'data-testid': 'discount-value-input',
            }}
            error={!!formErrors.discountValue}
            helperText={formErrors.discountValue}
            disabled={saving}
          />

          {/* Expiry date */}
          <TextField
            label="Expiry Date (optional)"
            value={form.expiresAt}
            onChange={setField('expiresAt')}
            fullWidth
            type="date"
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'discount-expiry-input' }}
            disabled={saving}
          />

          {/* Max uses */}
          <TextField
            label="Max Total Uses (optional — leave blank for unlimited)"
            value={form.maxUses}
            onChange={setField('maxUses')}
            fullWidth
            type="number"
            inputProps={{ min: 1, step: 1, 'data-testid': 'discount-max-uses-input' }}
            disabled={saving}
          />

          {/* Description */}
          <TextField
            label="Description (optional)"
            value={form.description}
            onChange={setField('description')}
            fullWidth
            multiline
            rows={2}
            inputProps={{ 'data-testid': 'discount-description-input' }}
            disabled={saving}
          />

          {/* Current uses — read-only, only shown in edit mode */}
          {isEdit && (
            <TextField
              label="Current Uses"
              value={editTarget!.currentUses}
              fullWidth
              InputProps={{ readOnly: true }}
              inputProps={{ 'data-testid': 'discount-current-uses-display' }}
              helperText="Read-only — updated automatically as codes are redeemed"
              size="small"
              sx={{ bgcolor: 'action.hover', borderRadius: 1 }}
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          data-testid="save-discount-btn"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// DiscountCodesPanel
// ---------------------------------------------------------------------------

export const DiscountCodesPanel: React.FC = () => {
  const [codes, setCodes] = useState<DiscountCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<DiscountCode | null>(null);
  const [deactivating, setDeactivating] = useState<string | number | null>(null);
  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(DISCOUNT_CODES_API);
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setCodes(data.codes ?? []);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load discount codes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCodes();
  }, [fetchCodes]);

  const handleAddClick = () => {
    setEditTarget(null);
    setDialogOpen(true);
  };

  const handleEditClick = (code: DiscountCode) => {
    setEditTarget(code);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditTarget(null);
  };

  const handleSaved = (saved: DiscountCode) => {
    setCodes(prev => {
      const idx = prev.findIndex(c => c.id === saved.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = saved;
        return next;
      }
      return [...prev, saved];
    });
    setDialogOpen(false);
    setEditTarget(null);
    setToast({ message: `Discount code "${saved.code}" saved successfully.`, severity: 'success' });
  };

  const handleDeactivate = async (code: DiscountCode) => {
    if (!window.confirm(`Deactivate "${code.code}"? This cannot be reversed in this sprint.`)) return;
    setDeactivating(code.id);
    try {
      const res = await authFetch(`${DISCOUNT_CODES_API}/${code.id}/deactivate`, {
        method: 'PATCH',
      });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const data = await res.json();
      setCodes(prev => prev.map(c => c.id === data.code.id ? data.code : c));
      setToast({ message: `"${code.code}" has been deactivated.`, severity: 'success' });
    } catch (err: unknown) {
      setToast({
        message: err instanceof Error ? err.message : 'Failed to deactivate code',
        severity: 'error',
      });
    } finally {
      setDeactivating(null);
    }
  };

  return (
    <Box data-testid="discount-codes-panel">
      {/* Toolbar */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          data-testid="add-discount-btn"
        >
          Add Code
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={fetchCodes}
          disabled={loading}
          data-testid="refresh-discount-btn"
        >
          Refresh
        </Button>
        {loading && <CircularProgress size={20} />}
      </Stack>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="discount-error-alert">
          {error}
        </Alert>
      )}

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table data-testid="discount-codes-table" aria-label="discount codes list">
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell align="right">Max Uses</TableCell>
                <TableCell align="right">Used</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && codes.length === 0 ? (
                <TableRow data-testid="discount-empty-row">
                  <TableCell colSpan={8} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>
                      No discount codes yet. Click "Add Code" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                codes.map((dc) => (
                  <TableRow
                    key={dc.id}
                    hover
                    data-testid={`discount-row-${dc.id}`}
                    sx={dc.isActive ? {} : { opacity: 0.6, bgcolor: 'action.disabledBackground' }}
                  >
                    <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {dc.code}
                    </TableCell>
                    <TableCell>{getTypeLabel(dc.discountType)}</TableCell>
                    <TableCell>{formatValue(dc.discountType, dc.discountValue)}</TableCell>
                    <TableCell>{formatExpiry(dc.expiresAt)}</TableCell>
                    <TableCell align="right">
                      {dc.maxUses != null ? dc.maxUses.toLocaleString() : 'Unlimited'}
                    </TableCell>
                    <TableCell align="right" data-testid={`discount-uses-${dc.id}`}>
                      {dc.currentUses.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={dc.isActive ? 'Active' : 'Inactive'}
                        color={dc.isActive ? 'success' : 'default'}
                        size="small"
                        data-testid={`discount-status-${dc.id}`}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEditClick(dc)}
                            data-testid={`edit-discount-${dc.id}`}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {dc.isActive && (
                          <Tooltip title="Deactivate">
                            <span>
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => handleDeactivate(dc)}
                                disabled={deactivating === dc.id}
                                data-testid={`deactivate-discount-${dc.id}`}
                              >
                                {deactivating === dc.id
                                  ? <CircularProgress size={16} />
                                  : <BlockIcon fontSize="small" />
                                }
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Create / Edit dialog */}
      <DiscountCodeFormDialog
        open={dialogOpen}
        editTarget={editTarget}
        onClose={handleDialogClose}
        onSaved={handleSaved}
      />

      {/* Toast notifications */}
      <Snackbar
        open={toast !== null}
        autoHideDuration={4000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast?.severity ?? 'success'}
          onClose={() => setToast(null)}
          data-testid="discount-toast"
          sx={{ width: '100%' }}
        >
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
